import React from 'react';
import { Link } from 'react-router-dom';
import { LandingFooter } from '../components/LandingFooter';
import {
  UsersIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

export const LandingPage: React.FC = () => {
  const WA_NUMBER = '573013354020';
  const WA_LINK = `https://wa.me/${WA_NUMBER}?text=Hola%20DHING%2C%20estoy%20interesado%20en%20DH%20Assembly%20para%20mi%20conjunto%20residencial`;

  const features = [
    {
      icon: UsersIcon,
      title: 'Quórum Legal Automático',
      desc: 'Cálculo en tiempo real basado estrictamente en los coeficientes de copropiedad. Cumple al 100% con la normativa de propiedad horizontal.',
    },
    {
      icon: ChartBarIcon,
      title: 'Votaciones Electrónicas Seguras',
      desc: 'Lanza mociones de votación al instante con ponderación por coeficiente. Resultados gráficos inmediatos y transparentes para todos.',
    },
    {
      icon: DocumentTextIcon,
      title: 'Generación de Actas PDF',
      desc: 'Al finalizar la sesión, el sistema compila automáticamente el acta con los resultados de votación, quórum y firmas digitales embebidas.',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Chat en Vivo Integrado',
      desc: 'Espacio de comunicación ordenado para intervenciones y constancias durante la asamblea, sincronizado mediante WebSockets.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Poderes y Delegación de Voto',
      desc: 'Flujo formal donde un propietario delega su representación y votos a otro propietario del mismo conjunto, generando el respaldo en PDF.',
    },
    {
      icon: SparklesIcon,
      title: 'Gestión Dinámica de Agenda',
      desc: 'Orden del día estructurado. Proyecta el punto actual en discusión para mantener el enfoque y optimizar el tiempo de la reunión.',
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Soporte Multi-Conjunto',
      desc: 'Ideal para administradores y empresas que gestionan varias propiedades. Controla todas tus comunidades desde una única cuenta principal.',
    },
    {
      icon: KeyIcon,
      title: 'Invitaciones y Accesos Claros',
      desc: 'Envío automático de credenciales y enlaces de conexión seguros por correo electrónico a todos los copropietarios importados.',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Carga tu Comunidad',
      desc: 'Configura las torres y apartamentos e importa fácilmente el listado de copropietarios y coeficientes desde una plantilla de Excel.',
    },
    {
      step: '02',
      title: 'Convoca la Asamblea',
      desc: 'Define fecha, hora y el orden del día. El sistema notifica automáticamente a los propietarios con sus enlaces de acceso personal.',
    },
    {
      step: '03',
      title: 'Sesión y Votación en Vivo',
      desc: 'Controla el quórum de asistencia en tiempo real, modera las participaciones y ejecuta votaciones seguras al instante.',
    },
    {
      step: '04',
      title: 'Acta Lista y Auditada',
      desc: 'Cierra la asamblea y descarga el acta final en PDF con un registro inmutable de decisiones, asistencias y representación legal.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Conjuntos Pequeños',
      units: 'Hasta 50 unidades',
      desc: 'Ideal para edificios residenciales y comunidades compactas.',
      price: 'Consultar',
      period: 'por asamblea',
      features: [
        'Asamblea virtual o presencial',
        'Importación de copropietarios',
        'Cálculo de quórum por coeficiente',
        'Votaciones y mociones ilimitadas',
        'Generación de acta final en PDF',
        'Soporte y acompañamiento previo',
      ],
      highlight: false,
    },
    {
      name: 'Conjuntos Medianos',
      units: '51 a 150 unidades',
      desc: 'La solución perfecta para conjuntos residenciales estándar.',
      price: 'Consultar',
      period: 'por asamblea',
      features: [
        'Todo lo del plan Pequeños',
        'Gestión avanzada de poderes entre propietarios',
        'Proyección en pantalla completa para asambleas mixtas',
        'Módulo de chat en vivo y registro de intervenciones',
        'Envío automatizado de convocatorias',
        'Soporte técnico prioritario durante el evento',
      ],
      highlight: true,
    },
    {
      name: 'Grandes Comunidades',
      units: 'Más de 150 unidades',
      desc: 'Diseñado para macroproyectos, ciudadelas y copropiedades extensas.',
      price: 'A medida',
      period: 'paquetes disponibles',
      features: [
        'Capacidad optimizada para alta concurrencia',
        'Múltiples asambleas ordinarias y extraordinarias',
        'Gestión multi-conjunto centralizada',
        'Auditoría completa y exportación de logs',
        'Capacitación a miembros del consejo y administración',
        'Ingeniero de soporte dedicado en vivo',
      ],
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased selection:bg-blue-600 selection:text-white">
      {/* ─── NAVBAR ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-base shadow-md shadow-blue-600/20 group-hover:scale-105 transition-transform">
              A
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900">
              DH <span className="text-blue-600">Assembly</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-blue-600/20 hover:bg-blue-700 transition-all hover:shadow hover:scale-[1.02] active:scale-[0.98]"
            >
              Iniciar Sesión
            </Link>
          </div>
        </div>
      </header>

      {/* ─── HERO SECTION ──────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32 border-b border-slate-200/60">
        {/* Decorative background glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-to-tr from-blue-600/10 to-indigo-600/5 rounded-full blur-[120px] pointer-events-none -z-10" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none -z-10" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-blue-50/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-700 mb-8 backdrop-blur-sm shadow-sm">
            <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
            Plataforma Profesional de Propiedad Horizontal
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-6">
            Gestiona tus asambleas{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              como un profesional
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-600 leading-relaxed mb-10">
            Control automático de quórum legal por coeficientes, votaciones electrónicas seguras en tiempo real y generación inmediata de actas PDF. Diseñado para garantizar transparencia y validez jurídica.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto sm:max-w-none">
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 py-4 text-base font-bold text-white shadow-lg shadow-blue-600/25 hover:bg-blue-700 transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
            >
              Solicitar Cotización
              <ArrowRightIcon className="w-5 h-5 stroke-[2.5]" />
            </a>
            <Link
              to="/manual"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-8 py-4 text-base font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-400 transition-all"
            >
              Ver Manual de Uso
            </Link>
          </div>

          <p className="mt-6 text-xs text-slate-500">
            Sin cobros mensuales recurrentes innecesarios. Contratación ágil por evento o por paquetes de asambleas.
          </p>
        </div>
      </section>

      {/* ─── HOW IT WORKS ──────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Flujo Operativo</h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Cuatro pasos para una asamblea exitosa
            </h3>
            <p className="mt-4 text-slate-600 text-base sm:text-lg">
              Una metodología probada que simplifica desde la planeación hasta la firma del acta final.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative group flex flex-col items-start">
                <div className="mb-4 flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 font-bold text-lg group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-300 shadow-sm">
                  {item.step}
                </div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES GRID ─────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-slate-50/50 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Capacidades del Sistema</h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Todo lo necesario para el cumplimiento legal
            </h3>
            <p className="mt-4 text-slate-600 text-base sm:text-lg">
              Herramientas diseñadas específicamente para resolver los retos de la propiedad horizontal en Colombia.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feat, index) => {
              const Icon = feat.icon;
              return (
                <div
                  key={index}
                  className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <div className="mb-4 inline-flex p-3 rounded-xl bg-blue-50 text-blue-600">
                      <Icon className="w-6 h-6 stroke-[2]" />
                    </div>
                    <h4 className="text-base font-bold text-slate-900 mb-2">{feat.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── ROLES DEL SISTEMA ─────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <div className="lg:pr-8">
              <h2 className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Estructura Clara</h2>
              <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
                Roles definidos para tu seguridad
              </h3>
              <p className="text-slate-600 text-base leading-relaxed mb-6">
                Para proteger la integridad jurídica de las decisiones, la plataforma opera bajo un modelo estricto de accesos autorizados, evitando intromisiones externas.
              </p>
              <div className="inline-flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200/80 p-4 text-sm text-amber-900 font-medium">
                <ShieldCheckIcon className="w-5 h-5 text-amber-600 shrink-0" />
                <span>Un propietario únicamente puede delegar su voto a otro propietario registrado en el padrón del mismo conjunto.</span>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Rol Admin */}
              <div className="rounded-2xl border-2 border-blue-600/10 bg-gradient-to-b from-white to-blue-50/30 p-8 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 translate-x-4 -translate-y-4 w-24 h-24 bg-blue-600/5 rounded-full pointer-events-none" />
                <span className="inline-block px-3 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-bold tracking-wide uppercase mb-4">
                  Administración
                </span>
                <h4 className="text-xl font-bold text-slate-900 mb-3">Administrador / Gerente</h4>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Tiene el control total del evento. Importa el padrón de copropietarios, lanza las preguntas de votación, monitorea el quórum legal en tiempo real y compila el acta definitiva.
                </p>
                <ul className="space-y-2 text-sm text-slate-700 font-medium">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-blue-600 shrink-0" /> Control de quórum y asistencias
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-blue-600 shrink-0" /> Lanzamiento y cierre de mociones
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-blue-600 shrink-0" /> Generación y firma de actas
                  </li>
                </ul>
              </div>

              {/* Rol Propietario */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <span className="inline-block px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-bold tracking-wide uppercase mb-4">
                  Comunidad
                </span>
                <h4 className="text-xl font-bold text-slate-900 mb-3">Propietario / Copropietario</h4>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Ingresa a un entorno optimizado para ejercer su derecho a voto con base en su coeficiente de propiedad. Puede participar en el chat oficial o ceder su poder formalmente a un vecino.
                </p>
                <ul className="space-y-2 text-sm text-slate-700 font-medium">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-600 shrink-0" /> Voto ponderado por coeficiente
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-600 shrink-0" /> Delegación interna segura
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-600 shrink-0" /> Acceso desde cualquier dispositivo
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PLANES DE PRECIOS ─────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-slate-50 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Inversión Transparente</h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Planes ajustados al tamaño de tu copropiedad
            </h3>
            <p className="mt-4 text-slate-600 text-base sm:text-lg">
              Sabemos que las asambleas no ocurren todos los meses. Paga únicamente por los eventos que realmente necesitas realizar.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 bg-white border flex flex-col justify-between transition-all relative ${
                  plan.highlight
                    ? 'border-blue-600 shadow-xl shadow-blue-600/5 ring-2 ring-blue-600/10 lg:-translate-y-2'
                    : 'border-slate-200 shadow-sm hover:border-slate-300'
                }`}
              >
                {plan.highlight && (
                  <span className="absolute top-0 right-8 -translate-y-1/2 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white uppercase tracking-wider shadow-sm">
                    Más Solicitado
                  </span>
                )}

                <div>
                  <div className="mb-6">
                    <h4 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h4>
                    <span className="inline-block px-2.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold mb-3">
                      {plan.units}
                    </span>
                    <p className="text-xs text-slate-500 leading-relaxed">{plan.desc}</p>
                  </div>

                  <div className="mb-6 pb-6 border-b border-slate-100 flex items-baseline gap-1.5">
                    <span className="text-3xl font-extrabold tracking-tight text-slate-900">{plan.price}</span>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      / {plan.period}
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <CheckCircleIcon className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={`${WA_LINK}%20-%20Plan%20${encodeURIComponent(plan.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full py-3 rounded-xl font-bold text-sm tracking-wide text-center transition-all block ${
                    plan.highlight
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/20'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  Solicitar Cotización
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SEGURIDAD Y CONFIANZA ─────────────────────────────────────── */}
      <section className="py-16 bg-white border-b border-slate-200/60 text-center">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-6">
            Garantías Técnicas y de Infraestructura
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 text-sm font-semibold text-slate-600">
            <span className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" /> Autenticación Segura 2FA
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" /> Control de Sesión Única
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" /> Encriptación SSL/TLS
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" /> Trazabilidad Inmutable
            </span>
          </div>
        </div>
      </section>

      {/* ─── FINAL CALL TO ACTION ──────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.1)_0,_transparent_100%)] pointer-events-none" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            ¿Listo para llevar tus asambleas al siguiente nivel?
          </h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Escríbenos directamente a nuestro WhatsApp para recibir asesoría personalizada y cotizar el servicio para tu copropiedad.
          </p>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 rounded-xl bg-white px-8 py-4 text-base font-bold text-blue-600 shadow-xl hover:bg-slate-50 transition-all hover:scale-105 active:scale-95"
          >
            Contactar por WhatsApp
            <ArrowRightIcon className="w-5 h-5 stroke-[2.5]" />
          </a>
          <p className="mt-4 text-xs text-blue-200/80">
            Atención directa gestionada por el equipo técnico de DHING.
          </p>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};
export default LandingPage;
